import React from "react";
import moment from "moment";
import _ from "lodash";

import {choiceValue, dateToMoment, momentToDate} from "../../utils";
import {decimalStrToNumber} from "../../../../../utils/utils";
import useModelWithAutoCalculatedField from "./useModelWithAutoCalculatedField";
import {DEPT_INSURANCE} from "../../constants";

const loanAutoCalculationHelper = {
  fieldName: 'value',
  getCalculationDeps: (model) => [
    model.original_value, model.start_date, model.fixed_interest_date, model.interest_rate, model.payment_rate
  ],
  getInitialModel: (defaults) => ({
    sub_category: choiceValue(defaults.sub_category),
    name: defaults.name,
    company: defaults.company,
    value: decimalStrToNumber(defaults.value),
    original_value: decimalStrToNumber(defaults.original_value),
    interest_rate: decimalStrToNumber(defaults.interest_rate),
    monthly_amount: decimalStrToNumber(defaults.monthly_amount),
    final_amount: decimalStrToNumber(defaults.final_amount),
    start_date: dateToMoment(defaults.start_date),
    end_date: dateToMoment(defaults.end_date),
    monthly_annuity: defaults.monthly_annuity,
    payment_rate: defaults.payment_rate,
    debt_insurance: choiceValue(defaults.debt_insurance) || DEPT_INSURANCE.no.value,
    fixed_interest_date: dateToMoment(defaults.fixed_interest_date),
    updated: dateToMoment(defaults.updated),
  }),
  depsFields: ['original_value', 'start_date', 'fixed_interest_date', 'interest_rate', 'payment_rate'],
  isAutoCalcAvailable: (model) => model.original_value && model.start_date && model.fixed_interest_date &&
    model.interest_rate && model.payment_rate,
  calculateFieldValue: (model) => {
    const today = moment();
    const start = model.start_date;
    const end = model.fixed_interest_date;

    const annualRate = model.interest_rate / 100;
    const annualRepayment = model.payment_rate / 100;
    const monthlyAnnuity = (model.original_value * (annualRate + annualRepayment)) / 12;
    const monthlyRate = annualRate / 12;

    // 2. Determine how many months to calculate (up to today)
    // We cap this at the targetDate so we don't calculate into the "future"
    const calculationPoint = today.isAfter(end) ? end : today;
    const monthsDiff = calculationPoint.diff(start, 'months');

    let value = model.original_value;
    let monthlyInterestAmount, monthlyAmount = null;

    // 3. Iterate month by month
    for (let i = 0; i < monthsDiff; i++) {
      monthlyInterestAmount = _.round(value * monthlyRate, 2);

      monthlyAmount = _.round(monthlyAnnuity - monthlyInterestAmount, 2);

      value = _.round(value - monthlyAmount, 2);

      if(value < 0){
        value = 0;
        break;
      }
    }

    return {monthlyAnnuity, monthlyAmount, value}
  }
};

const useLoanModel = (defaults, handleFormChange) => {
  const [model, handleModelChange, calculated, isAutoCalcAvailable] = useModelWithAutoCalculatedField(
    loanAutoCalculationHelper, defaults
  );
  let monthlyAnnuity = model.monthly_annuity,
    monthlyAmount = model.monthly_amount,
    value = model.value;

  if(model.is_auto_calculation && calculated !== null){
    ({monthlyAnnuity, monthlyAmount, value} = calculated);
  }

  React.useEffect(() => {
    handleFormChange && handleFormChange({
      ...model,
      monthly_annuity: monthlyAnnuity,
      monthly_amount: monthlyAmount,
      value: value,
      start_date: momentToDate(model.start_date),
      end_date: momentToDate(model.end_date),
      fixed_interest_date: momentToDate(model.fixed_interest_date)
    });
  }, [model, monthlyAnnuity, monthlyAmount, value]);

  return [model, handleModelChange, isAutoCalcAvailable, monthlyAnnuity, monthlyAmount, value];
};

export default useLoanModel;